import React, { useEffect, useRef, useState } from "react"
import { ReactSVG } from "react-svg"
import { TrafficInfoBlockFieldsFragment } from "../../../../graphql-types"
import { infoTrafficBlockPicto } from "../../../../static/images"
import { pushGtmEvent } from "../../../gtm/interaction-event"
import { configureDecorativeSVGAccessibility } from "../../../utils/accessibility"
import { getStatus, LuasStatusLine } from "../../../api/luas-forecasts-api"
import {
  getLuasLineDisplayName,
  getLuasLineStatusLabel,
  getLuasUiLabels,
  LuasUiLabelsRecord,
} from "../../luas/luas-ui-labels"
import { mockLuasStatusLines } from "../../luas/luas-status-mock"
import { configureSVGAccessibility } from "../../../utils/accessibility"
import * as inputsStyles from "../../layout/inputs.module.scss"
import * as globalBlockStyles from "../main-blocks/main-blocks.module.scss"
import * as parentStyles from "../traffic-block/traffic-parent-block.module.scss"
import * as styles from "./luas-travel-update-block.module.scss"

type RenderProps = {
  block: TrafficInfoBlockFieldsFragment
  intersectionRatioCallback: (ratio: number) => void
  locale?: string
  luasUiLabels?: LuasUiLabelsRecord | null
}

// Homepage card for Luas: shows live Red/Green line status instead of manually entered CMS text.
const LuasTravelUpdateBlock: React.FC<RenderProps> = ({ block, intersectionRatioCallback, luasUiLabels }) => {
  const uiLabels = getLuasUiLabels(luasUiLabels)
  const component = useRef<HTMLElement>(null)
  const observer = useRef<IntersectionObserver>(null)
  const intersectionRatioCallbackRef = useRef(intersectionRatioCallback)
  const [lines, setLines] = useState<LuasStatusLine[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    intersectionRatioCallbackRef.current = intersectionRatioCallback
  }, [intersectionRatioCallback])

  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      return
    }
    const options = { threshold: [0.2, 0.4, 0.6, 0.8, 1.0] }
    observer.current = new IntersectionObserver(entries => {
      intersectionRatioCallbackRef.current(entries[entries.length - 1].intersectionRatio)
    }, options)
    observer.current.observe(component.current)
    return () => observer.current.disconnect()
  }, [])

  useEffect(() => {
    let cancelled = false
    getStatus()
      .then(result => {
        if (!cancelled) setLines(result.lines)
      })
      .catch(fetchError => {
        console.error("[luas] Status API unavailable", fetchError)
        if (cancelled) return
        // The live API blocks CORS on localhost; show mock data there so the design can still be previewed.
        if (process.env.NODE_ENV === "development" && window.location.hostname === "localhost") {
          setLines(mockLuasStatusLines)
        } else {
          setFailed(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleLinkClick = () => pushGtmEvent("eventga", "Homepage", "Infos Trafic - Plus d'Infos", block.title)

  // Fall back to the manually entered CMS text if the live API is unavailable.
  if (failed) {
    return (
      <section className={`${parentStyles.block} ${parentStyles.customBlock}`} ref={component}>
        <h2 className={parentStyles.title}>{block.title}</h2>
        {block.image.format === "svg" ? (
          <ReactSVG
            src={block.image.url}
            className={parentStyles.svgImage}
            beforeInjection={svg => configureSVGAccessibility(svg, block.image.alt || "")}
          />
        ) : (
          <img src={block.image.url} className={parentStyles.image} alt={block.image.alt || ""} />
        )}
        <p className={parentStyles.trafficStatusLabel}>{block.normalTrafficLabel}</p>
        <a
          className={`${parentStyles.link} ${inputsStyles.primaryButton}`}
          href={block.internalLink.slug}
          aria-label={`${block.linkLabel} - ${block.title}`}
          onClick={handleLinkClick}
        >
          {block.linkLabel}
        </a>
      </section>
    )
  }

  return (
    <section
      className={`${parentStyles.block} ${parentStyles.customBlock} ${globalBlockStyles.mainBlocksCard}`}
      ref={component}
    >
      <div className={parentStyles.blockHeader}>
        <ReactSVG
          src={infoTrafficBlockPicto}
          className={parentStyles.blockPicto}
          beforeInjection={configureDecorativeSVGAccessibility}
        />
        <h2 className={parentStyles.title}>{block.title}</h2>
      </div>

      <ul className={styles.lineStatuses} aria-live="polite">
        {lines === null ? (
          <li>{uiLabels.loadingTravelUpdates}</li>
        ) : (
          lines.map(line => (
            <li key={line.name} className={styles.lineStatus}>
              <span
                className={`${styles.lineName} ${line.name.toLowerCase().includes("red") ? styles.redLine : styles.greenLine}`}
              >
                {getLuasLineDisplayName(line.name, uiLabels)}
              </span>
              <span className={line.operatingNormally ? styles.badgeNormal : styles.badgeDisrupted}>
                {getLuasLineStatusLabel(line.operatingNormally, uiLabels)}
              </span>
              <span className={styles.lineMessage}>
                {line.linewideMessage || getLuasLineStatusLabel(line.operatingNormally, uiLabels)}
              </span>
            </li>
          ))
        )}
      </ul>

      <a
        className={`${parentStyles.link} ${inputsStyles.primaryButton}`}
        href={block.internalLink.slug}
        aria-label={`${block.linkLabel} - ${block.title}`}
        onClick={handleLinkClick}
      >
        {block.linkLabel}
      </a>
    </section>
  )
}

export default LuasTravelUpdateBlock
